/**
 * ProductsSection — the PRODUCTS content block's widget, in two shapes:
 *
 * 1. SHOWCASE — products carrying their own commerce fields (price, link,
 *    description, CTA label) render as a card rail, each card a direct link.
 * 2. RETAILER — products without per-product links but with a retailers list
 *    render the carousel + "Shop Online" drawer (the retailer-exclusives
 *    pattern), where the retailer, not the product, owns the destination.
 *
 * Everything rendered comes from the block's config. This widget previously
 * shipped hardcoded default products and retailers from its first campaign;
 * a block with no data now renders nothing, which is the correct claim.
 */

import React, { useState, useCallback, useEffect } from "react";
import {
    FaChevronLeft,
    FaChevronRight,
    FaShoppingCart,
    FaExternalLinkAlt,
    FaTimes,
    FaMapMarkerAlt,
    FaGift,
} from "react-icons/fa";
import "./ProductsSection.scss";
import {ExternalLink, isExternalHref} from "@/widgets/ExternalLink";

interface Product {
    id: string;
    name: string;
    imageUrl?: string;
    /** Pre-formatted display price string. Never used for arithmetic. */
    price?: string;
    /** Per-product shop link — presence makes the card its own CTA. */
    productUrl?: string;
    description?: string;
    /** The buy CTA's label; "Shop" when unset. */
    ctaText?: string;
}

interface Retailer {
    id: string;
    name: string;
    logoUrl?: string;
    productUrl: string;
    icon?: React.ReactNode;
    type: "online" | "in-store" | "both";
    exclusive?: string;
}

interface ProductsSectionProps {
    products?: Product[];
    retailers?: Retailer[];
    sectionTitle?: string;
    subtitle?: string;
    /** When set, the kicker becomes a link. Absent leaves it as plain text. */
    subtitleUrl?: string;
}

export const ProductsSection = ({
    products = [],
    retailers = [],
    sectionTitle,
    subtitle,
    subtitleUrl,
}: ProductsSectionProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [activeTab, setActiveTab] = useState<"online" | "in-store">("online");

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === "Escape" && isDrawerOpen) {
            setIsDrawerOpen(false);
            setSelectedProduct(null);
        }
    }, [isDrawerOpen]);

    useEffect(() => {
        if (isDrawerOpen) {
            document.addEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "";
        };
    }, [isDrawerOpen, handleKeyDown]);

    if (!products.length) {
        return null;
    }

    /*
     * A single product with its own link makes this a showcase — mixing the
     * two shapes in one block reads as two different stores.
     */
    const isShowcase = products.some((p) => p.productUrl || p.price || p.description);

    const title = sectionTitle || "Products";

    if (isShowcase) {
        return (
            <section className="products-section products-section--showcase">
                <div className="products-section__head">
                    <h2 className="products-section__title">{title}</h2>
                    {subtitle && subtitleUrl &&
                        <p className="products-section__subtitle">
                            <a
                                className="products-section__subtitle-link"
                                href={subtitleUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <FaShoppingCart size={12} /> {subtitle}
                            </a>
                        </p>
                    }
                    {subtitle && !subtitleUrl &&
                        <p className="products-section__subtitle">
                            <FaShoppingCart size={12} /> {subtitle}
                        </p>
                    }
                </div>
                <div className="products-section__rail">
                    {products.map((p) => {
                        const body =
                            <>
                                {p.imageUrl
                                    ? <span className="products-section__card-img" style={{backgroundImage: `url("${p.imageUrl}")`}} />
                                    : <span className="products-section__card-img products-section__card-img--empty" aria-hidden="true" />
                                }
                                <span className="products-section__card-body">
                                    <span className="products-section__card-name">{p.name}</span>
                                    {p.description &&
                                        <span className="products-section__card-desc">{p.description}</span>
                                    }
                                    <span className="products-section__card-row">
                                        {p.price && <b className="products-section__card-price">{p.price}</b>}
                                        {p.productUrl &&
                                            <span className="products-section__card-cta">{p.ctaText || "Shop"}</span>
                                        }
                                    </span>
                                </span>
                            </>;
                        /*
                         * Through ExternalLink like every other link here, for
                         * its scheme guard: `productUrl` is admin-authored and
                         * reaches this component unvalidated, so a `javascript:`
                         * value rendered as a clickable card.
                         */
                        return isExternalHref(p.productUrl)
                            ? <ExternalLink
                                key={p.id}
                                className="products-section__card products-section__card--link"
                                href={p.productUrl ?? ""}
                            >
                                {body}
                            </ExternalLink>
                            : <div key={p.id} className="products-section__card">{body}</div>;
                    })}
                </div>
            </section>
        );
    }

    /* Retailer-exclusives shape: carousel + drawer. Needs retailers to sell through. */
    const currentProduct = products[currentIndex];

    const handlePrev = () => {
        setCurrentIndex((prev) => prev === 0 ? products.length - 1 : prev - 1);
    };

    const handleNext = () => {
        setCurrentIndex((prev) => prev === products.length - 1 ? 0 : prev + 1);
    };

    const handleShopOnline = (product: Product) => {
        setSelectedProduct(product);
        setIsDrawerOpen(true);
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            setIsDrawerOpen(false);
            setSelectedProduct(null);
        }
    };

    const onlineRetailers = retailers.filter((r) => r.type === "online" || r.type === "both");
    const inStoreRetailers = retailers.filter((r) => r.type === "in-store" || r.type === "both");

    return (
        <section className="products-section">
            <h2 className="products-section__title">{title}</h2>

            <div className="products-section__carousel">
                <button
                    className="products-section__nav"
                    onClick={handlePrev}
                    aria-label="Previous product"
                >
                    <FaChevronLeft size={18} />
                </button>

                <div className="products-section__card">
                    {currentProduct.imageUrl &&
                        <img
                            src={currentProduct.imageUrl}
                            alt={currentProduct.name}
                            className="products-section__product-image"
                        />
                    }
                    <h3 className="products-section__product-name">
                        {currentProduct.name}
                    </h3>
                    {retailers.length > 0 &&
                        <button
                            className="products-section__shop-btn"
                            onClick={() => handleShopOnline(currentProduct)}
                        >
                            <span>SHOP ONLINE</span>
                            <FaChevronRight size={12} />
                        </button>
                    }
                </div>

                <button
                    className="products-section__nav"
                    onClick={handleNext}
                    aria-label="Next product"
                >
                    <FaChevronRight size={18} />
                </button>
            </div>

            <div className="products-section__dots">
                {products.map((_, idx) =>
                    <button
                        key={idx}
                        className={`products-section__dot ${idx === currentIndex ? "products-section__dot--active" : ""}`}
                        onClick={() => setCurrentIndex(idx)}
                        aria-label={`Go to product ${idx + 1}`}
                    />)}
            </div>

            {isDrawerOpen && selectedProduct &&
                <div
                    className="products-section__drawer-overlay"
                    onClick={handleOverlayClick}
                >
                    <div className="products-section__drawer">
                        <div className="products-section__drawer-header">
                            <div className="products-section__drawer-product">
                                {selectedProduct.imageUrl &&
                                    <img
                                        src={selectedProduct.imageUrl}
                                        alt={selectedProduct.name}
                                        className="products-section__drawer-product-img"
                                    />
                                }
                                <div>
                                    <h3 className="products-section__drawer-product-name">
                                        {selectedProduct.name}
                                    </h3>
                                    <p className="products-section__drawer-subtitle">
                                        Select a retailer to purchase
                                    </p>
                                </div>
                            </div>
                            <button
                                className="products-section__drawer-close"
                                onClick={() => {
                                    setIsDrawerOpen(false);
                                    setSelectedProduct(null);
                                }}
                                aria-label="Close"
                            >
                                <FaTimes size={16} />
                            </button>
                        </div>

                        <div className="products-section__drawer-tabs">
                            <button
                                className={`products-section__drawer-tab ${activeTab === "online" ? "products-section__drawer-tab--active" : ""}`}
                                onClick={() => setActiveTab("online")}
                            >
                                <FaShoppingCart size={14} />
                                Shop Online
                            </button>
                            <button
                                className={`products-section__drawer-tab ${activeTab === "in-store" ? "products-section__drawer-tab--active" : ""}`}
                                onClick={() => setActiveTab("in-store")}
                            >
                                <FaMapMarkerAlt size={14} />
                                Find In Store
                            </button>
                        </div>

                        <div className="products-section__drawer-content">
                            {(activeTab === "online" ? onlineRetailers : inStoreRetailers).map((retailer) =>
                                <ExternalLink
                                    key={retailer.id}
                                    className="products-section__retailer-btn"
                                    href={retailer.productUrl ?? ""}
                                >
                                    <div className="products-section__retailer-info">
                                        <div className="products-section__retailer-btn-icon">
                                            {retailer.logoUrl
                                                ? <img src={retailer.logoUrl} alt={retailer.name} />
                                                : retailer.icon || <FaShoppingCart size={18} />
                                            }
                                        </div>
                                        <div className="products-section__retailer-btn-details">
                                            <span className="products-section__retailer-name">
                                                {retailer.name}
                                            </span>
                                            {retailer.exclusive &&
                                                <span className="products-section__retailer-bonus">
                                                    <FaGift size={10} /> {retailer.exclusive}
                                                </span>
                                            }
                                        </div>
                                    </div>
                                    <FaExternalLinkAlt size={12} className="products-section__retailer-arrow" />
                                </ExternalLink>)}
                        </div>

                        <div className="products-section__drawer-footer">
                            <p>Verify your purchase to earn bonus XP!</p>
                        </div>
                    </div>
                </div>
            }
        </section>
    );
};
